import * as React from 'react';
import { GripVertical, Settings2, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { Separator } from '@/components/ui/Separator';
import { cn } from '@/lib/utils';
import type { QColumnDef, QColumnConfig } from './types';

interface QColumnCustomizerProps<T> {
  columns: QColumnDef<T>[];
  configs: QColumnConfig[];
  onChange: (configs: QColumnConfig[]) => void;
}

function QColumnCustomizer<T>({
  columns,
  configs,
  onChange,
}: QColumnCustomizerProps<T>) {
  const [dragIndex, setDragIndex] = React.useState<number | null>(null);

  const columnMap = React.useMemo(
    () => new Map(columns.map((c) => [c.key, c])),
    [columns]
  );

  const sortedConfigs = React.useMemo(
    () => [...configs].sort((a, b) => a.order - b.order),
    [configs]
  );

  const visibleCount = configs.filter((c) => c.visible).length;

  const toggleVisibility = (key: string) => {
    const col = columnMap.get(key);
    if (col?.locked) return;
    onChange(
      configs.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c))
    );
  };

  const showAll = () => {
    onChange(configs.map((c) => ({ ...c, visible: true })));
  };

  const handleDragStart = (index: number) => {
    const col = columnMap.get(sortedConfigs[index].key);
    if (col?.locked) return;
    setDragIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === targetIndex) return;

    const newConfigs = [...sortedConfigs];
    const [moved] = newConfigs.splice(dragIndex, 1);
    newConfigs.splice(targetIndex, 0, moved);

    const reordered = newConfigs.map((c, i) => ({ ...c, order: i }));
    onChange(reordered);
    setDragIndex(targetIndex);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5">
          <Settings2 className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Columnas</span>
          <span className="text-xs text-muted-foreground">
            {visibleCount}/{columns.length}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-0">
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-sm font-medium">Columnas</span>
          {visibleCount < columns.length && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={showAll}>
              Mostrar todas
            </Button>
          )}
        </div>
        <Separator />
        <div className="max-h-64 overflow-y-auto py-1">
          {sortedConfigs.map((config, index) => {
            const col = columnMap.get(config.key);
            if (!col) return null;
            return (
              <div
                key={config.key}
                draggable={!col.locked}
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={cn(
                  'flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-muted/50 transition-colors',
                  dragIndex === index && 'opacity-50',
                  col.locked && 'opacity-60'
                )}
              >
                <span className={cn('cursor-grab', col.locked && 'cursor-not-allowed opacity-30')}>
                  <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
                </span>
                <button
                  type="button"
                  className="flex items-center gap-2 flex-1 text-left"
                  onClick={() => toggleVisibility(config.key)}
                  disabled={col.locked}
                >
                  {config.visible ? (
                    <Eye className="h-3.5 w-3.5 text-foreground" />
                  ) : (
                    <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                  <span className={cn(!config.visible && 'text-muted-foreground')}>
                    {col.header}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { QColumnCustomizer };
